/**
 * Chapter designer: the shared level-design layer above every game's generator.
 *
 * Levels 1–20 are each game's hand-tuned ramp (tutorial, new elements one by one). From level 21 on, every block
 * of 20 levels is a *designed chapter*: instead of a flat "a bit harder each level" ramp that stops growing around
 * level 30, a chapter has a shape (warm-up, build-ups, a hard level every fifth, a breather after it, a boss at the
 * end), its own difficulty range that rises chapter by chapter up to the game's ceiling, and a twist — one of the
 * game's existing variations that leads the chapter (e.g. Raf: a chapter of flag shelves).
 *
 * The designer never builds a level itself. It tells the game's generator which of the game's own levels to take
 * the difficulty from (`effective`: a level number inside [floor, ceiling] of the game's ramp) and which twist to
 * apply; the generator keeps building, seeding and validating (bot) as before, seeded by the real level number so
 * the content is new. Same level number → same design for every player (H3: difficulty comes only from level
 * design and the seed). Pure and deterministic: only seeded randomness (Rng).
 */
import { Rng, hashSeed } from "./rng.js";

/** First designed level and chapter length. */
export const DESIGN_FROM = 21;
export const DESIGN_CHAPTER = 20;

export type SlotRole = "warmup" | "build" | "hard" | "breather" | "boss";

/**
 * Shape of every designed chapter: role and difficulty relative to the chapter's range (0 = its floor, 1 = its
 * top). Hard levels at 5, 10 and 15 are each followed by a breather; the boss closes the chapter.
 */
export const CHAPTER_SHAPE: readonly (readonly [SlotRole, number])[] = [
  ["warmup", 0.1],
  ["warmup", 0.2],
  ["build", 0.35],
  ["build", 0.45],
  ["hard", 0.75],
  ["breather", 0.15],
  ["build", 0.4],
  ["build", 0.5],
  ["build", 0.58],
  ["hard", 0.85],
  ["breather", 0.25],
  ["build", 0.48],
  ["build", 0.58],
  ["build", 0.65],
  ["hard", 0.9],
  ["breather", 0.3],
  ["build", 0.6],
  ["build", 0.7],
  ["build", 0.78],
  ["boss", 1],
];

export interface DesignSpec {
  /** Game id: keys the twist order. */
  game: string;
  /**
   * The game's own levels that bound the design: the easiest designed slot plays like level `floor` (past the
   * tutorial special cases), the hardest like `ceiling` (where the game's ramp stops growing).
   */
  floor: number;
  ceiling: number;
  /** The game's chapter twists (existing variations, not new mechanics). Empty: shape and range only. */
  twists?: readonly string[];
}

export interface DesignedLevel {
  level: number;
  /** 1-based designed chapter (levels 21–40 are chapter 1). */
  chapter: number;
  /** 1..20 inside the chapter. */
  index: number;
  role: SlotRole;
  /** 0..1 across the game's range, after the chapter's own range is applied. */
  difficulty: number;
  /** The game's level whose difficulty parameters this level uses. */
  effective: number;
  /** The chapter's twist, or null. */
  twist: string | null;
  /** Share of the game's bot runs that should win this level: easy slots demand more. */
  winRate: { target: number; min: number; max: number };
}

export function isDesigned(level: number): boolean {
  return Number.isInteger(level) && level >= DESIGN_FROM;
}

/** 1-based designed chapter of a level (0 for the hand-tuned levels 1–20). */
export function designChapterOf(level: number): number {
  return isDesigned(level) ? Math.floor((level - DESIGN_FROM) / DESIGN_CHAPTER) + 1 : 0;
}

/** First and last level of a designed chapter. */
export function designChapterRange(chapter: number): [number, number] {
  const first = DESIGN_FROM + (Math.max(1, chapter) - 1) * DESIGN_CHAPTER;
  return [first, first + DESIGN_CHAPTER - 1];
}

/**
 * The chapter's difficulty range on the game's 0..1 scale: it starts a little under the hand-tuned ramp's end and
 * rises 0.05 per chapter until breathers sit at 0.6 and every boss is at the game's ceiling (from chapter 4 on).
 */
export function chapterRange(chapter: number): { lo: number; hi: number } {
  const step = Math.max(0, chapter - 1) * 0.05;
  return { lo: Math.min(0.6, 0.35 + step), hi: Math.min(1, 0.85 + step) };
}

/** The game's twist for a chapter: a fixed per-game shuffle, used in turn; never the same twice in a row. */
export function chapterTwist(spec: DesignSpec, chapter: number): string | null {
  const twists = spec.twists ?? [];
  if (!twists.length) return null;
  const order = twists.slice();
  const rng = new Rng(hashSeed(`designer:${spec.game}`));
  for (let i = order.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [order[i], order[j]] = [order[j]!, order[i]!];
  }
  return order[(Math.max(1, chapter) - 1) % order.length]!;
}

/** Role of a level in its chapter (the same for every game), or null for levels 1–20. For the map's badges. */
export function designRole(level: number): SlotRole | null {
  if (!isDesigned(level)) return null;
  return CHAPTER_SHAPE[(level - DESIGN_FROM) % DESIGN_CHAPTER]![0];
}

/** The design of one level, or null for the hand-tuned levels 1–20. */
export function designLevel(spec: DesignSpec, level: number): DesignedLevel | null {
  if (!isDesigned(level)) return null;
  const chapter = designChapterOf(level);
  const index = level - designChapterRange(chapter)[0] + 1;
  const [role, rel] = CHAPTER_SHAPE[index - 1]!;
  const { lo, hi } = chapterRange(chapter);
  const difficulty = Math.round((lo + rel * (hi - lo)) * 1000) / 1000;
  const floor = Math.max(1, Math.floor(spec.floor));
  const ceiling = Math.max(floor, Math.floor(spec.ceiling));
  const effective = Math.round(floor + difficulty * (ceiling - floor));
  const target = Math.round((0.92 - 0.42 * difficulty) * 100) / 100;
  return {
    level,
    chapter,
    index,
    role,
    difficulty,
    effective,
    twist: chapterTwist(spec, chapter),
    winRate: { target, min: Math.max(0, Math.round((target - 0.15) * 100) / 100), max: Math.min(1, Math.round((target + 0.15) * 100) / 100) },
  };
}

/** All 20 levels of a designed chapter (for reports, the map and tests). */
export function designChapter(spec: DesignSpec, chapter: number): DesignedLevel[] {
  const [first] = designChapterRange(chapter);
  return CHAPTER_SHAPE.map((_, i) => designLevel(spec, first + i)!);
}
